import React, { useState, useEffect } from 'react';
import Navbar from '../components/Navbar';
import { getCommands, updateCommand, toggleCommand, extractErrorMessage } from '../services/api';
import { FaCog, FaSave, FaToggleOn, FaToggleOff, FaLock, FaClock } from 'react-icons/fa';
import { useToast } from '../components/Toast';
import '../styles/Commands.css';

function Commands({ user }) {
  const toast = useToast();
  const [commands, setCommands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingCommand, setEditingCommand] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({});

  useEffect(() => {
    fetchCommands();
  }, []);

  const fetchCommands = async () => {
    try {
      setLoading(true);
      const data = await getCommands();
      setCommands(data);
    } catch (error) {
      console.error('Error fetching commands:', error);
      toast.error(extractErrorMessage(error, 'Befehle konnten nicht geladen werden'));
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (commandName) => {
    try {
      await toggleCommand(commandName);
      toast.success('Befehl umgeschaltet');
      fetchCommands();
    } catch (error) {
      console.error('Error toggling command:', error);
      toast.error(extractErrorMessage(error, 'Fehler beim Umschalten des Befehls'));
    }
  };

  const handleEdit = (command) => {
    setEditingCommand(command.command_name);
    setFormData({
      description: command.description || '',
      requiredPermission: command.required_permission || '',
      cooldown: command.cooldown || 0,
      allowedChannels: (command.allowedChannels || []).join(', '),
      allowedRoles: (command.allowedRoles || []).join(', ')
    });
  };

  const handleSave = async () => {
    if (!editingCommand || saving) return;
    setSaving(true);

    try {
      const updateData = {
        description: formData.description,
        requiredPermission: formData.requiredPermission || null,
        cooldown: parseInt(formData.cooldown) || 0,
        allowedChannels: formData.allowedChannels
          ? formData.allowedChannels.split(',').map(s => s.trim()).filter(Boolean)
          : [],
        allowedRoles: formData.allowedRoles
          ? formData.allowedRoles.split(',').map(s => s.trim()).filter(Boolean)
          : []
      };

      await updateCommand(editingCommand, updateData);
      setEditingCommand(null);
      setFormData({});
      toast.success('Befehl aktualisiert');
      fetchCommands();
    } catch (error) {
      console.error('Error updating command:', error);
      toast.error(extractErrorMessage(error, 'Fehler beim Aktualisieren des Befehls'));
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setEditingCommand(null);
    setFormData({});
  };

  if (loading) {
    return (
      <div>
        <Navbar user={user} />
        <div className="loading-container">
          <div className="spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="app-layout">
      <Navbar user={user} />
      <main className="main-content">
        <div className="page-header">
          <h1>Command Configuration</h1>
          <p>Manage Discord bot commands settings</p>
        </div>

        {!user.isAdmin && (
          <div className="alert alert-info">
            <FaLock /> You need admin permissions to modify command settings
          </div>
        )}

        <div className="commands-grid">
          {commands.map((command) => (
            <div key={command.command_name} className="command-card">
              <div className="command-header">
                <div className="command-title">
                  <FaCog />
                  <h3>/{command.command_name}</h3>
                  <span className={`status-badge ${command.enabled ? 'enabled' : 'disabled'}`}>
                    {command.enabled ? 'Enabled' : 'Disabled'}
                  </span>
                </div>
                {user.isAdmin && (
                  <button
                    onClick={() => handleToggle(command.command_name)}
                    className={`toggle-btn ${command.enabled ? 'enabled' : 'disabled'}`}
                    title={command.enabled ? 'Disable command' : 'Enable command'}
                  >
                    {command.enabled ? <FaToggleOn /> : <FaToggleOff />}
                  </button>
                )}
              </div>

              {editingCommand === command.command_name ? (
                <div className="command-edit-form">
                  <div className="form-group">
                    <label>Description</label>
                    <input
                      type="text"
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      placeholder="Command description"
                    />
                  </div>

                  <div className="form-group">
                    <label>Required Permission</label>
                    <input
                      type="text"
                      value={formData.requiredPermission}
                      onChange={(e) => setFormData({ ...formData, requiredPermission: e.target.value })}
                      placeholder="e.g., ManageMessages"
                    />
                  </div>

                  <div className="form-group">
                    <label>Cooldown (seconds)</label>
                    <input
                      type="number"
                      value={formData.cooldown}
                      onChange={(e) => setFormData({ ...formData, cooldown: e.target.value })}
                      min="0"
                    />
                  </div>

                  <div className="form-group">
                    <label>Allowed Channels (comma-separated IDs)</label>
                    <input
                      type="text"
                      value={formData.allowedChannels}
                      onChange={(e) => setFormData({ ...formData, allowedChannels: e.target.value })}
                      placeholder="123456789, 987654321"
                    />
                  </div>

                  <div className="form-group">
                    <label>Allowed Roles (comma-separated IDs)</label>
                    <input
                      type="text"
                      value={formData.allowedRoles}
                      onChange={(e) => setFormData({ ...formData, allowedRoles: e.target.value })}
                      placeholder="123456789, 987654321"
                    />
                  </div>

                  <div className="form-actions">
                    <button onClick={handleSave} className="save-btn" disabled={saving}>
                      <FaSave /> {saving ? 'Speichert\u2026' : 'Save'}
                    </button>
                    <button onClick={handleCancel} className="cancel-btn" disabled={saving}>
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="command-details">
                  <p className="command-description">{command.description}</p>

                  <div className="command-meta">
                    {command.required_permission && (
                      <div className="meta-item">
                        <FaLock />
                        <span>Permission: {command.required_permission}</span>
                      </div>
                    )}
                    {command.cooldown > 0 && (
                      <div className="meta-item">
                        <FaClock />
                        <span>Cooldown: {command.cooldown}s</span>
                      </div>
                    )}
                  </div>

                  {(command.allowedChannels?.length > 0 || command.allowedRoles?.length > 0) && (
                    <div className="command-restrictions">
                      {command.allowedChannels?.length > 0 && (
                        <p>📢 Channels: {command.allowedChannels.length} configured</p>
                      )}
                      {command.allowedRoles?.length > 0 && (
                        <p>👥 Roles: {command.allowedRoles.length} configured</p>
                      )}
                    </div>
                  )}

                  {user.isAdmin && (
                    <button
                      onClick={() => handleEdit(command)}
                      className="edit-btn"
                    >
                      <FaCog /> Configure
                    </button>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </main>
    </div>
  );
}

export default Commands;
